'use memo';

import { createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { useCallback, useEffect, useMemo } from 'react';
import { FillScreen, HStack } from '@/components/Layout';
import { ViewportLoader } from '@/components/Loading';
import { Text } from '@/components/Typography/Text';
import { stacktraceNS, useTranslation } from '@/lib/i18n';
import { useSuspenseQueryOperationPackageInstance } from '../../-queries';
import { OrganizeTicketsOperation } from './-components/OrganizeTicketsOperation';
import { SelectTargetTicketsOperation } from './-components/SelectTargetTicketsOperation';
import { SelectTargetValuesOperation } from './-components/SelectTargetValuesOperation';
import { resolveDefaultOrganizeTicketsTask } from './-utils/resolveOrganizeTicketsTask';
import { findSelectTaskById, resolveDefaultSelectTask, resolveNextInProgressSelectTask } from './-utils/resolveSelectTask';
import { SearchSchema } from './-zod-schema';
export const Route = createFileRoute('/_stacktrace/planning-operations-core/operation-packages/$id/patterns/$patternId/instances/$instanceId/_layout/data/')({
  component: RouteComponent,
  validateSearch: zodValidator(SearchSchema)
});

/**
 * PN オペレーションコア インスタンス詳細「データ」タブ。
 *
 * packageType で遂行内容を出し分ける（QA データタブと同型）:
 * - TicketReconciliation: Step2 `OrganizeTickets`（チケット一覧の diff 表示 + 完了）
 * - それ以外（リリースプラン作成）: 後続タスク SelectTargetTickets /
 *   SelectTargetValues の複数選択グリッド
 */
function RouteComponent() {
  const params = Route.useParams();
  const {
    instanceId
  } = params;
  const {
    data: instance
  } = useSuspenseQueryOperationPackageInstance(instanceId);
  if (instance.packageType === 'TicketReconciliation') {
    return <TicketReconciliationDataTab />;
  }
  return <ReleasePlanDataTab />;
}

/**
 * 開発チケット棚卸しデータタブ（Step2 `OrganizeTickets`）。
 *
 * 走行中インスタンスの対象プロダクトに紐づく開発チケット一覧を表示し、
 * 「チケットの整理を完了」で専用エンドポイントを叩いて完了する。完了後は
 * detail タブへ戻す（PP「価値の棚卸し」のデータタブと同型）。Step1（アサイン）
 * は詳細タブのタスクカード内でインライン完了するためデータタブでは扱わない。
 */
function TicketReconciliationDataTab() {
  const {
    t
  } = useTranslation(stacktraceNS, {
    keyPrefix: 'planningOperationsCore.ticketReconciliation.instance.data'
  });
  const params = Route.useParams();
  const {
    instanceId
  } = params;
  const navigate = Route.useNavigate();
  const {
    data: instance
  } = useSuspenseQueryOperationPackageInstance(instanceId);
  const steps = instance.operationSteps;
  const organizeTask = useMemo(() => resolveDefaultOrganizeTicketsTask(steps), [steps]);
  const handleCompleted = useCallback(() => {
    navigate({
      to: '/planning-operations-core/operation-packages/$id/patterns/$patternId/instances/$instanceId/detail',
      params
    });
  }, [navigate, params]);
  if (!organizeTask) {
    return <FillScreen className="flex items-center justify-center">
        <Text variant="label" className="text-neutral-muted-on-fill">
          {t('noTask')}
        </Text>
      </FillScreen>;
  }
  return <FillScreen>
      <OrganizeTicketsOperation instanceId={instanceId} taskId={organizeTask.task.id} isCompleted={organizeTask.task.status === 'Completed'} onCompleted={handleCompleted} />
    </FillScreen>;
}

/**
 * リリースプラン作成インスタンス詳細「データ」タブ。
 *
 * IT「デバイス管理ラベル発行」のデータタブと同じ構成で、
 * 後続タスク SelectTargetTickets / SelectTargetValues を遂行する。
 * - task code で候補テーブル（チケット/価値）を振り分ける
 * - 候補を複数選択し dock の完了アクションで execute API を呼ぶ
 * - 完了後は次の InProgress 選択タスクへ URL を更新する
 */
function ReleasePlanDataTab() {
  const {
    t
  } = useTranslation(stacktraceNS, {
    keyPrefix: 'planningOperationsCore.releasePlanCreation.instance.data'
  });
  const params = Route.useParams();
  const {
    instanceId
  } = params;
  const navigate = Route.useNavigate();
  const searchParams = Route.useSearch();
  const {
    data: instance
  } = useSuspenseQueryOperationPackageInstance(instanceId);
  const steps = instance.operationSteps;

  // 初回マウント時、operationTaskInstanceId 未設定なら InProgress の選択タスクを注入
  useEffect(() => {
    if (searchParams.operationTaskInstanceId) return;
    const defaultTask = resolveDefaultSelectTask(steps);
    if (defaultTask) {
      navigate({
        search: prev => ({
          ...prev,
          operationTaskInstanceId: defaultTask.task.id
        }),
        replace: true
      });
    }
  }, [searchParams.operationTaskInstanceId, steps, navigate]);

  // 現在表示中の選択タスクを解決
  const current = useMemo(() => findSelectTaskById(steps, searchParams.operationTaskInstanceId), [steps, searchParams.operationTaskInstanceId]);

  // 完了後: 次の InProgress 選択タスクへ URL を進める（無ければ detail へ）
  const handleExecuted = useCallback(() => {
    if (!current) return;
    const next = resolveNextInProgressSelectTask(steps, current.task.id);
    if (next) {
      navigate({
        search: prev => ({
          ...prev,
          operationTaskInstanceId: next.task.id,
          freeText: undefined,
          pageIndex: 0,
          includeCompleted: undefined
        }),
        replace: true
      });
    } else {
      navigate({
        to: '/planning-operations-core/operation-packages/$id/patterns/$patternId/instances/$instanceId/detail',
        params
      });
    }
  }, [current, steps, navigate, params]);
  const onSearch = useCallback((searchUpdate: {
    freeText?: string;
    pageIndex?: number;
    includeCompleted?: boolean;
  }) => {
    navigate({
      search: prev => ({
        ...prev,
        ...searchUpdate
      }),
      replace: true
    });
  }, [navigate]);

  // 注入待ち（選択タスクがまだ解決されていない）
  if (!searchParams.operationTaskInstanceId) {
    return <ViewportLoader />;
  }

  // 選択タスクが見つからない（対象選択タスクが存在しない等）
  if (!current) {
    return <FillScreen className="flex items-center justify-center">
        <Text variant="label" className="text-neutral-muted-on-fill">
          {t('noSelectTask')}
        </Text>
      </FillScreen>;
  }
  const isCompleted = current.task.status === 'Completed';
  const pageIndex = searchParams.pageIndex ?? 0;
  return <FillScreen>
      <HStack className="h-full overflow-hidden">
        {current.kind === 'tickets' ? <SelectTargetTicketsOperation instanceId={instanceId} taskInstanceId={current.task.id} isCompleted={isCompleted} freeText={searchParams.freeText} pageIndex={pageIndex} includeCompleted={searchParams.includeCompleted} onSearch={onSearch} onExecuted={handleExecuted} /> : <SelectTargetValuesOperation instanceId={instanceId} taskInstanceId={current.task.id} isCompleted={isCompleted} freeText={searchParams.freeText} pageIndex={pageIndex} onSearch={onSearch} onExecuted={handleExecuted} />}
      </HStack>
    </FillScreen>;
}